import { useLocation } from "wouter";
import { User, KeyRound, LogOut, HelpCircle, UserCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface UserMenuProps {
  onChangePassword: () => void;
  onLogout: () => void;
  userName?: string;
  /** Optional pre-formatted jurisdiction badge (e.g. "🇨🇦 ON") shown next to the name. */
  jurisdiction?: string;
  /** Optional — shows "Edit Profile" item when provided (admin only) */
  onEditProfile?: () => void;
}

export function UserMenu({ onChangePassword, onLogout, userName, jurisdiction, onEditProfile }: UserMenuProps) {
  const [, setLocation] = useLocation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" data-testid="button-user-menu">
          <User className="w-4 h-4" />
          {userName && <span className="ml-2 max-w-32 truncate hidden sm:inline">{userName}</span>}
          {jurisdiction && (
            <span className="ml-1.5 text-muted-foreground" data-testid="text-user-jurisdiction">{jurisdiction}</span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>My Account</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => setLocation("/support")}
          data-testid="menu-item-support"
        >
          <HelpCircle className="w-4 h-4 mr-2" />
          Support
        </DropdownMenuItem>
        {onEditProfile && (
          <DropdownMenuItem
            onClick={onEditProfile}
            data-testid="menu-item-edit-profile"
          >
            <UserCircle className="w-4 h-4 mr-2" />
            Edit Profile
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={onChangePassword}
          data-testid="menu-item-change-password"
        >
          <KeyRound className="w-4 h-4 mr-2" />
          Change Password
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onLogout}
          data-testid="menu-item-logout"
          className="text-destructive focus:text-destructive"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
